import { useEffect, useRef } from 'react';
import {
  isLoginEntry,
  isNoBackScreen,
  isPostLogin,
  pushPrev,
  topTarget,
  pop,
} from 'lib/backNavigationUtils';

/**
 * useBackNavigation
 * Tracks a simple back-stack and returns canShowBack + handler.
 * @param {string|null} screen - The current screen
 * @param {Function} track - Analytics tracker
 * @param {Function} setScreen - Updates the current screen
 */
export function useBackNavigation(screen, track, setScreen) {
  const historyRef = useRef([]);
  const prevScreenRef = useRef(null);
  const skipPushFromRef = useRef(null);

  // build the back stack as screens change
  useEffect(() => {
    const prev = prevScreenRef.current;
    if (prev !== null && prev !== screen) {
      if (skipPushFromRef.current === prev) {
        skipPushFromRef.current = null;
      } else {
        historyRef.current = pushPrev(historyRef.current, prev, screen);
      }
    }
    prevScreenRef.current = screen;
  }, [screen]);

  /**
   * Computes the screen to navigate back to.
   * @returns {string|null} The target screen to navigate back to, or null if none.
   */
  const computeTargetWithFallback = () => {
    let target = topTarget(historyRef.current, screen);
    if (!target && prevScreenRef.current && prevScreenRef.current !== screen) {
      const lastScreen = prevScreenRef.current;
      if (!(isPostLogin(screen) && (isLoginEntry(lastScreen)))) {
        target = lastScreen;
      }
    }
    return target;
  };

  const canShowBack = !isNoBackScreen(screen) && !!computeTargetWithFallback();

  /**
   * Handles back button clicks (finds target, tracks event, updates history, and navigates.)
  */
  const handleBackButtonClick = () => {
    const target = computeTargetWithFallback();
    if (!target) return;

    track?.({
      action: 'SignIn:Click',
      value: 'BackButton',
      screen,
      targetScreen: target,
    });

    const historyTarget = topTarget(historyRef.current, screen);
    if (historyTarget === target) {
      historyRef.current = pop(historyRef.current);
    }

    skipPushFromRef.current = screen;
    setScreen?.(target);
  };

  return { canShowBack, handleBackButtonClick };
}
